import React, { useMemo, useState, useCallback, useEffect } from "react";
import DataTable from "@/global/tables/DataTable";
import { getUserManagementColumns } from "./UserManagementColumns";
import UserDetailModal from "./UserDetailModal";
import Loader from "../../common/Loader/Loader";
import { handleCatch } from "../../../shared/helpers";
import useRoles from "../../../hooks/useRoles";
import {
  fetchUserManagementList,
  fetchUserManagementDetail,
  setUserManagementActive,
  toggleUserMedicationActivation,
  patchUserManagement,
  buildUserPatchPayloadFromForm,
} from "../../../services/userManagement";

const ALL = { value: null, label: "All" };

const medicationOptions = [
  ALL,
  { value: "Lenalidomide", label: "Lenalidomide" },
  { value: "Pomalidomide", label: "Pomalidomide" },
];

const activeAccountOptions = [
  ALL,
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

function replaceUserInList(list, updatedUser) {
  return list.map((row) => (row.id === updatedUser.id ? updatedUser : row));
}

const UserManagement = () => {
  const { canChangeUser, canManageUserMedication, canViewHospitals } = useRoles();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionUserId, setActionUserId] = useState(null);
  const [medication] = useState(medicationOptions[0]);
  const [activeFilter] = useState(activeAccountOptions[0]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saveSaving, setSaveSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [productActionId, setProductActionId] = useState(null);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const users = await fetchUserManagementList();
      setRows(users);
    } catch (error) {
      handleCatch(error);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredRows = useMemo(() => {
    let list = [...rows];
    const med = medication?.value;
    if (med) {
      list = list.filter((r) =>
        (r.products ?? []).some((p) => p.name === med)
      );
    }
    const act = activeFilter?.value;
    if (act === "active") {
      list = list.filter((r) => r.active_account);
    } else if (act === "inactive") {
      list = list.filter((r) => !r.active_account);
    }
    return list;
  }, [rows, medication, activeFilter]);

  const applyUserUpdate = useCallback((updatedUser) => {
    setRows((prev) => replaceUserInList(prev, updatedUser));
    setSelectedUser((current) =>
      current?.id === updatedUser.id ? updatedUser : current
    );
  }, []);

  const onDeactivate = useCallback(
    async (row) => {
      setActionUserId(row.id);
      try {
        const updated = await setUserManagementActive(row, false);
        applyUserUpdate(updated);
      } catch (error) {
        handleCatch(error);
      } finally {
        setActionUserId(null);
      }
    },
    [applyUserUpdate]
  );

  const onActivate = useCallback(
    async (row) => {
      setActionUserId(row.id);
      try {
        const updated = await setUserManagementActive(row, true);
        applyUserUpdate(updated);
      } catch (error) {
        handleCatch(error);
      } finally {
        setActionUserId(null);
      }
    },
    [applyUserUpdate]
  );

  const onToggleProduct = useCallback(
    async (user, product) => {
      if (!canManageUserMedication) return;
      const medicationId = product.id;
      if (!medicationId) return;

      setProductActionId(medicationId);
      try {
        await toggleUserMedicationActivation(medicationId);
        const refreshed = await fetchUserManagementDetail(user.id);
        applyUserUpdate(refreshed);
      } catch (error) {
        handleCatch(error);
      } finally {
        setProductActionId(null);
      }
    },
    [applyUserUpdate, canManageUserMedication]
  );

  const columns = useMemo(
    () =>
      getUserManagementColumns({
        onDeactivate,
        onActivate,
        actionUserId,
        canChangeUser,
      }),
    [onDeactivate, onActivate, actionUserId, canChangeUser]
  );

  const handleRowClick = useCallback(async (row) => {
    setSelectedUser(row);
    setDetailLoading(true);
    try {
      const detail = await fetchUserManagementDetail(row.id);
      if (detail) setSelectedUser(detail);
    } catch (error) {
      handleCatch(error);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedUser(null);
    setDetailLoading(false);
  }, []);

  const handleSaveUser = useCallback(
    async (form) => {
      if (!selectedUser?.id) return;
      setSaveSaving(true);
      try {
        const updated = await patchUserManagement(
          selectedUser.id,
          buildUserPatchPayloadFromForm(form)
        );
        applyUserUpdate(updated);
      } catch (error) {
        handleCatch(error);
        throw error;
      } finally {
        setSaveSaving(false);
      }
    },
    [selectedUser, applyUserUpdate]
  );

  if (isLoading && rows.length === 0) {
    return <Loader />;
  }

  return (
    <div className="p-8" data-testid="user-management-page">
      <div className="mx-auto max-w-[1600px]">
        <div
          className="rounded-3xl bg-white px-4 shadow-sm"
          data-testid="user-management-table"
        >
          <div className="p-4">
            <DataTable
              columns={columns}
              data={filteredRows}
              tableTitle="Admin user management"
              loading={isLoading}
              onRowClick={handleRowClick}
              getRowTestId={(row) => `user-management-row-${row?.id}`}
            />
          </div>
        </div>
      </div>

      <UserDetailModal
        open={Boolean(selectedUser)}
        user={selectedUser}
        loading={detailLoading}
        saving={saveSaving}
        productActionId={productActionId}
        canChangeUser={canChangeUser}
        canManageUserMedication={canManageUserMedication}
        canViewHospitals={canViewHospitals}
        onClose={handleCloseModal}
        onSave={handleSaveUser}
        onActivateProduct={(user, product) => onToggleProduct(user, product)}
        onDeactivateProduct={(user, product) => onToggleProduct(user, product)}
      />
    </div>
  );
};

export default UserManagement;
